/*
 * Validações usuais
 */

const REGEX_CNPJ_VALIDO = /^[A-Z0-9]{12}\d{2}$/;
const REGEX_REPETICAO = /^([A-Z0-9])\1{13}$/;
const REGEX_REMOVE_MASCARA = /[^A-Z0-9]/g;
const TAMANHO_BASE = 12;
const PESOS_MODULO_11 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
const BASE_ASCII = 48;

/**
 * Valida se o CNPJ é válido
 */
export function validateCnpj(cnpj = '') {
  if (cnpj.length === 0) {
    return true;
  }

  // Remove caracteres não alfanuméricos e converte para maiúsculo
  const cnpjSemMascara = cnpj.toUpperCase().replace(REGEX_REMOVE_MASCARA, '');

  // Validação estrutural e bloqueio de "falsos positivos" (ex: 00000000000000)
  if (!REGEX_CNPJ_VALIDO.test(cnpjSemMascara) || REGEX_REPETICAO.test(cnpjSemMascara)) {
    return false;
  }

  let somatorioDV1 = 0;
  let somatorioDV2 = 0;

  for (let i = 0; i < TAMANHO_BASE; i++) {
    const valorNumerico = cnpjSemMascara.charCodeAt(i) - BASE_ASCII;
    somatorioDV1 += valorNumerico * PESOS_MODULO_11[i + 1];
    somatorioDV2 += valorNumerico * PESOS_MODULO_11[i];
  }

  // Cálculo e verificação do 1º Dígito (DV1)
  const restoDV1 = somatorioDV1 % 11;
  const dv1Calculado = restoDV1 < 2 ? 0 : 11 - restoDV1;

  // O 1º dígito calculado passa a compor a soma do 2º dígito
  somatorioDV2 += dv1Calculado * PESOS_MODULO_11[TAMANHO_BASE];

  // Cálculo e verificação do 2º Dígito (DV2)
  const restoDV2 = somatorioDV2 % 11;
  const dv2Calculado = restoDV2 < 2 ? 0 : 11 - restoDV2;

  // Validação Final
  const digitosCalculados = `${dv1Calculado}${dv2Calculado}`;
  const digitosInformados = cnpjSemMascara.slice(-2);

  return digitosInformados === digitosCalculados;
}

export function validateCpf(cpf) {
  if (cpf.length === 0) {
    return true;
  }
  cpf = cpf.replace(/[^\d]+/g, '');
  if (cpf === '') return false;
  // Elimina CPFs invalidos conhecidos
  if (
    cpf.length !== 11 ||
    cpf === '00000000000' ||
    cpf === '11111111111' ||
    cpf === '22222222222' ||
    cpf === '33333333333' ||
    cpf === '44444444444' ||
    cpf === '55555555555' ||
    cpf === '66666666666' ||
    cpf === '77777777777' ||
    cpf === '88888888888' ||
    cpf === '99999999999'
  )
    return false;
  // Valida 1o digito
  let add = 0;
  for (let i = 0; i < 9; i++) add += parseInt(cpf.charAt(i)) * (10 - i);
  let rev = 11 - (add % 11);
  if (rev === 10 || rev === 11) rev = 0;
  if (rev !== parseInt(cpf.charAt(9))) return false;
  // Valida 2o digito
  add = 0;
  for (let i = 0; i < 10; i++) add += parseInt(cpf.charAt(i)) * (11 - i);
  rev = 11 - (add % 11);
  if (rev === 10 || rev === 11) rev = 0;
  if (rev !== parseInt(cpf.charAt(10))) return false;
  return true;
}

/**
 * Valida se um valor é um cpf ou cnpj
 *
 * @param {string} value valor a ser testado
 * @returns {boolean} true caso o valor seja um cpf ou cnpj
 */
export function validateCpfCnpj(value) {
  return validateCnpj(value) || validateCpf(value);
}

// Código CIB (Cadastro Imobiliário Brasileiro): 7 caracteres mais o dígito verificador.
// Algoritmo do Identificador do Cafir, da Receita Federal:
// https://www.gov.br/receitafederal/pt-br/assuntos/orientacao-tributaria/cadastros/cafir/identificador-do-cafir

const TABELA_BASE32_CIB = '0123456789ABCDEFGHJKMNPQRSTVWXYZ'; // base 32 de Crockford, sem I, L, O e U
const PESOS_CIB_ALFANUMERICO = [4, 3, 9, 5, 7, 1, 8];
const PESOS_CIB_NUMERICO = [8, 7, 6, 5, 4, 3, 2];
const TAMANHO_BASE_CIB = 7;
const TAMANHO_CIB = 8;

/**
 * Remove separadores, passa para maiúsculo e resolve os caracteres fora da tabela: pela
 * decodificação de Crockford, I e L valem 1 e O vale 0. O U não tem substituto.
 */
export function normalizarCodigoCib(codigoCib = '') {
  return `${codigoCib}`
    .replace(/[^0-9A-Za-z]/g, '')
    .toUpperCase()
    .replace(/[IL]/g, '1')
    .replace(/O/g, '0');
}

/**
 * @param {string} base os 7 caracteres que antecedem o dígito verificador, já normalizados
 * @returns {string|null} o dígito verificador, ou null se a base não for utilizável
 */
export function calcularDigitoVerificadorCib(base = '') {
  if (base.length !== TAMANHO_BASE_CIB || [...base].some((c) => !TABELA_BASE32_CIB.includes(c))) {
    return null;
  }

  if (/^\d+$/.test(base)) {
    const soma = [...base].reduce((acc, c, i) => acc + Number(c) * PESOS_CIB_NUMERICO[i], 0);
    const resto = soma % 11;

    return `${resto === 0 || resto === 1 ? 0 : 11 - resto}`;
  }

  const soma = [...base].reduce((acc, c, i) => acc + TABELA_BASE32_CIB.indexOf(c) * PESOS_CIB_ALFANUMERICO[i], 0);

  return TABELA_BASE32_CIB.charAt(soma % 31);
}

export function validateCodigoCib(codigoCib = '') {
  const normalizado = normalizarCodigoCib(codigoCib);

  if (normalizado.length !== TAMANHO_CIB) {
    return false;
  }

  return calcularDigitoVerificadorCib(normalizado.slice(0, TAMANHO_BASE_CIB)) === normalizado.slice(TAMANHO_BASE_CIB);
}

export function validateCep(cep = '') {
  cep = cep.replace(/\D+/g, '');

  return cep.length === 8;
}

export const validateEmail = (value = '') => {
  const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(value);
};

export const validatePhone = (value = '') => {
  const digits = `${value}`.replace(/\D+/g, '').length;

  if (value.includes('+')) {
    return digits >= 12 && digits <= 13;
  }
  return digits >= 10 && digits <= 11;
};

export const validateDate = (value = '') => {
  const re = /^([0-2][0-9]|(3)[0-1])(\/)(((0)[0-9])|((1)[0-2]))(\/)\d{4}$/;
  return re.test(value);
};

export const isEmpty = (obj) => {
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) return false;
  }
  return true;
};

export const diferenteDe = (option, value) => {
  if (option && value) {
    return option.toString().toLowerCase() !== value.toString().toLowerCase();
  }
  return true;
};
